import { Box, Button, Card, CardActions, CardContent, Chip, FormControl, IconButton, InputAdornment, MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./LicenceGenerator.module.css";
import { monthEnding, toIsoString } from "utils/date";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale/ru';
import { format, parse } from "date-fns";
import { AppsOutlined, Done, Equalizer, FileUpload, ShoppingBagOutlined } from "@mui/icons-material";
import { Tabs } from "components/layout";
import Apps from "./Apps";
import Profiles from "./Profiles";
import ApiIcon from '@mui/icons-material/Api';
import { NumberField, RangeField } from "components/forms";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { useGridApiRef } from "@mui/x-data-grid";
import { CircularProgress } from "components/overlays";
import { ConfirmationDialog, InfoDialog } from "components/dialogs";
import { useCreateLicencesMutation } from "services/data/licenceApi";
import { HidingError } from "components/errors";
import { UploadIconButton } from "components/buttons";
import { makeTable } from "./exportTable";

const dateFormat = "dd MMMM yyyy";
const referenceDate = new Date(1970, 0, 1, 0, 0, 0);
const durationDefaultDate = (() => {
  const date = new Date();
  return date.setFullYear(date.getFullYear() + 2, 0, 1);
})();

const message = (num) => {
    if(num === '') return {prefix: 'Будут сгенерированы', subject: 'лицензии'};

    let result = "";
    if (((num - 1) % 10 === 0) && ((num-11) % 100 !==0)) result = {prefix: 'Будет сгенерирована', subject: 'лицензия'};
    else if (
    ((num - 2) % 10 === 0 || (num - 3) % 10 === 0 || (num - 4) % 10 === 0) &&
    ((num - 11) % 100 !== 0 && (num - 12) % 100 !== 0 && (num - 13) % 100 !== 0 && (num - 14) % 100 !== 0)
  )
    result = {prefix: 'Будут сгенерированы', subject: 'лицензии'};
  else result = {prefix: 'Будут сгенерированы', subject: 'лицензий'};

  return result;
};

const postMessage = (type, start, end, file) => {
  switch(type) {
    case 'range': return ` для касс из диапазона заводских номеров ${start} - ${end} `;
    case 'file': return `для касс из загруженного файла ${file?.name} `;
    default: return ''; 
  }
};



const DEFAULT_ERROR = "Ошибка соединения с сервером";

const LicenceGenerator = memo(({profiles, apps, resetFitersAndSort}) => {

  const [duration, setDuration] = useState(12);
  const [quantity, setQuantity] = useState(1);
  const [maxDate, setMaxDate] = useState(format(durationDefaultDate, dateFormat));
  const [isValidDate, setValidDate] = useState(true);
  const [isValidSubject, setValidSubject] = useState(false);
  const [activeTab, setActiveTab] = useState({index: 0, name: 'prof'});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [quantityType, setQuantityType] = useState('quantity');

  const [currentStart, setCurrentStart] = useState('');
  const [currentEnd, setCurrentEnd] = useState('');
  const [focusedStart, setFocusedStart] = useState(false);
  const [focusedEnd, setFocusedEnd] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentFileType, setCurrentFileType] = useState('mts');

  const [createLicences, {data: createLicencesData, error: createLicencesError, reset: createLicencesReset, isLoading: createLicencesLoading}] = useCreateLicencesMutation();
  const dateMaxDate = parse( maxDate, dateFormat, referenceDate );

  const appsRef = useRef(null);
  const profilesRef = useRef(null);
  const gridRefs = {
    app: useGridApiRef(),
    prof: useGridApiRef()
  };


  useEffect(() => {
    for(let key of Object.keys(gridRefs)) {
      if(gridRefs[key].current === null) {
        gridRefs[key].current = {};   
      }
    }
  }, [gridRefs]);

  const subjectChange = (value) => { 
    setValidSubject(!!value?.length);
  };

  const renderDurationItems = useMemo(() => {
    const res = [];
    for(let i=1; i<13; i++) {
      res.push(<MenuItem key={i} value={i}>{i} {monthEnding(i)}</MenuItem>);
    };
    return res;
  }, []);
  
  
  const getNameFromItem = useCallback((data, id) => {
    return data?.find(item => item.id === id)?.name;
  }, []);

  const openNewTab = useCallback((body) => {  
    let items="";
    body?.items?.forEach(item => {
      const factoryNum = item?.status?.match(/\d+/)
      const data = item?.subjectType === "prof" ? profiles : apps;
      items += `<tr>
                  <td>${factoryNum ? factoryNum[0] : 'Не определён'}</td>
                  <td>${item.key}</td>
                  <td>${getNameFromItem(data, item.subject.at(0)?.id) ?? 'Не определён'}</td>
                </tr>`;
    });

    const htmlContent = makeTable(items);
    
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    window.open(url, "_blank");
  }, [profiles, apps]);


  const resetParams = () => {
    setCurrentStart('');
    setCurrentEnd('');
    setDuration(12);
    setQuantity(1);
    setMaxDate(format(durationDefaultDate, dateFormat));
    setActiveTab({index: 0, name: 'prof'});
    setQuantityType('quantity');
    setCurrentFile(null);
    gridRefs[activeTab.name]?.current?.setRowSelectionModel([]);
  };

  const getLicenceParams = () => {

    const formData = new FormData();
    formData.append('duration', String(duration));
    formData.append('endActivationAt', toIsoString(parse(maxDate, dateFormat, referenceDate)));
    
    switch(quantityType) {
      case 'quantity':
        formData.append('quantity', String(quantity));
        break;
      case 'range': 
        formData.append('range', JSON.stringify({start: currentStart, end: currentEnd}));
        break;
      case 'file':
        formData.append('file', currentFile);                  
    }

    const grid = [...gridRefs[activeTab.name]?.current?.getSelectedRows()].map( (value, index) => value[0]);
    if(grid.length) {
      formData.append('subjectType', activeTab.name);
      const subject = (activeTab.name === 'prof') ? grid.map((item) => ({id: item})) : grid.map((item) => ({package: item}));
      formData.append('subject', JSON.stringify(subject));
    }
    
    return formData;
  };

  const isValidQuantity = () => (quantity > 0);
  
  const isValidRange = () => {
    const _start = parseInt(currentStart); 
    if((currentStart.length < 8) || isNaN(_start) || _start < 0) return false;
    const _end = parseInt(currentEnd);
    if((currentEnd.length < 8) || isNaN(_end) || _end < 0) return false;

    if(_start > _end) return false;

    return true;
  };

  const isValidQuantityType = () => {
    switch(quantityType) {
      case 'quantity': return isValidQuantity();
      case 'range': return isValidRange();
      case 'file': return currentFile;
      default: return false;
    }
  };

  const isGenerateButtonDisabled = () => { 
    return !isValidDate || !isValidSubject || !isValidQuantityType();
  };

  const generate = () => {
    const params = getLicenceParams();
    setOpenConfirmDialog(false);
    createLicences({body:params}).unwrap()
      .then(() => { 
        setOpenInfoDialog(true);        
      })
      .catch(e => console.log(e));
  };

  const onChangeDate = (dateObject, context) => {
    setValidDate(!context.validationError);
    setMaxDate( format( dateObject, dateFormat ) );
  };

  const getTargetName = () => {
    
    if(gridRefs[activeTab.name].current && Object.keys(gridRefs[activeTab.name].current).length) {
      return [...gridRefs[activeTab.name]?.current?.getSelectedRows()].map( (value) => value.at(1).name).at(0);
    }
  };

  const getTargetTypeText = (type) => {
    if(type === 'prof') return ' профиля ';
    else if(type === 'app') return ' приложения ';
    return '';
  };

  const openError = () => {
    return !!createLicencesError || false;
  };

  const getErrorMessage = () => { 
    if(createLicencesError) return createLicencesError?.data?.message ?? DEFAULT_ERROR;
    else return'Ошибка';
  };

  const hideError = () => {
    if(createLicencesError) createLicencesReset();
  };

  const changeTypeQuantity = (event) => setQuantityType(event.target.value); 
  
  const renderQuantityType = useCallback( () => {
    switch(quantityType) {
      case "quantity": 
        return renderQuantity;
      case "range": 
        return renderRange;
      case "file":
        return renderFile;    
    }
  }, [quantityType, currentFile, quantity, currentEnd, currentStart, focusedEnd, focusedStart]);

  const renderQuantity = useMemo(() => {
    return <TextField
              value={quantity}
              size="small"
              inputProps={{
                isAllowed: ({floatValue}) => (floatValue < 10001 && floatValue > 0)
              }}
              InputProps={{
                inputComponent: NumberField,
                endAdornment: <InputAdornment position="end">
                                <Stack>
                                  <IconButton sx={{padding: '1px'}} disabled={quantity>=10000} onClick={() => setQuantity((old) => old + 1)}>
                                    <FontAwesomeIcon fontSize={'0.9rem'} icon={faCaretUp} />
                                  </IconButton>
                                  <IconButton sx={{padding: '1px'}} disabled={quantity<=1}onClick={() => setQuantity((old) => old - 1)}>
                                    <FontAwesomeIcon fontSize={'0.9rem'}icon={faCaretDown} />
                                  </IconButton>
                                </Stack>
                              </InputAdornment>

              }}
              onChange={setQuantity}
              sx={{width: '50%', marginLeft: '8px', minWidth: 250}}
            />;
  }, [quantity]);

  const renderRange = useMemo(() => {
    return  <Stack direction="row" spacing={1} sx={{width: '50%', marginLeft: '8px', minWidth: 250, alignItems: 'center'}}>
              <TextField 
                size="small"
                label="Начало диапазона"
                value={currentStart}
                error={currentStart.length > 0 && currentStart.length < 8}
                inputProps={{style: {textAlign: 'center', fontFamily: 'PT Mono'}}}
                InputProps={{
                  inputComponent: RangeField
                }}
                InputLabelProps={{
                  shrink: focusedStart || Boolean(currentStart)
                }}
                onFocus={() => setFocusedStart(true)}
                onBlur={() => setFocusedStart(false)}
                onChange={(e) => setCurrentStart(e.target.value)}
              />
              <Typography>-</Typography>
              <TextField
                size="small"
                label="Конец диапазона"
                value={currentEnd}
                error={currentEnd.length > 0 && currentEnd.length < 8}
                inputProps={{style: {textAlign: 'center', fontFamily: 'PT Mono'}}}
                InputProps={{
                  inputComponent: RangeField
                }}
                InputLabelProps={{
                  shrink: focusedEnd || Boolean(currentEnd)
                }}
                onFocus={() => setFocusedEnd(true)}
                onBlur={() => setFocusedEnd(false)}
                onChange={(e) => setCurrentEnd(e.target.value)}
              />
            </Stack>;
  }, [currentEnd, currentStart, focusedEnd, focusedStart]);

  const isMaxDateDisabled = useMemo( () => {
    return quantityType === 'file' && currentFileType === 'mts';
  }, [quantityType]);

  const renderFile = useMemo( () => {
    return  <Stack direction="row" spacing={1} sx={{width: '50%', marginLeft: '8px', minWidth: 250, alignItems: 'center'}}>
              <Select size="small" className="label" value={currentFileType} sx={{flexGrow: 1, marginRight: '8px'}}>
                  <MenuItem value='mts'>Формат МТС</MenuItem>
              </Select>
              {currentFile ? <Chip label='Файл загружен' icon={<Done/>} size='small'/> : null}
              <UploadIconButton
                    tip={currentFile ? "Загрузить другой файл" : "Загрузить файл"}
                    color="secondary"
                    sx={{backgroundColor: 'rgba(156, 39, 176, 0.08)', marginLeft: '16px'}}
                    iconCmp={<FileUpload/>}
                    handler={(e, clear) => e?.target?.files.length && setCurrentFile(e.target.files[0])} 
                  />
            </Stack>
  }, [currentFile]);
  
  const getQuantity = () => {
    switch(quantityType) {
      case 'quantity': return quantity;
      case 'range': return (parseInt(currentEnd) - parseInt(currentStart) + 1);
      case 'file': return '';
    }
  }

  const dialogText = () => {
    return <span>
            {message(getQuantity()).prefix} <b>{getQuantity()}</b> {message(getQuantity()).subject} для
              {getTargetTypeText(activeTab.name)}<b>{getTargetName()}</b> {postMessage(quantityType, currentStart, currentEnd, currentFile)} 
                длительностью <b>{duration}</b> {monthEnding(duration)}
          </span>;
  };

  const infoText = (body) => {
    return <span>
            Сгенерировано лицензий: {body?.total}
          </span>;
  };

  const infoHandler = (params) => {
    
    if(params.openInfoTab === 'on') {
      openNewTab(createLicencesData?.body);
    }
    setOpenInfoDialog(false);
    resetParams();
    resetFitersAndSort();
  };

  return (
    <div className={styles.details}>
        <HidingError 
          open={ openError() }
          message={ getErrorMessage() }
          onClose={hideError}
        />
        <CircularProgress open={createLicencesLoading}/>
        <div className={styles.content}>
          <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
            <CardContent sx={{overflow: 'auto'}}>
              <div className={styles.title}><Typography align="center" variant="h5">Генератор лицензий</Typography></div>
              <Stack direction="row"  mt={1} sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden'}}>
                <Typography className="label">Срок действия лицензии</Typography>
                <FormControl sx={{minWidth: 250 }}>
                  <Select
                    value={duration}
                    size="small"
                    onChange={(e) => setDuration(e.target.value)}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {renderDurationItems}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden', marginTop: '12px'}}>
                <Typography disabled={true} className="label">Предельная дата активации лицензии</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
                  <DatePicker
                    format="dd MMMM yyyy"
                    slotProps={{
                      field: {
                        size: 'small'
                      }
                    }}
                    disabled={isMaxDateDisabled}
                    value={isMaxDateDisabled ? null : dateMaxDate}
                    disablePast
                    onChange={onChangeDate}
                    sx={{minWidth: 250}}/>
                </LocalizationProvider>
              </Stack>
              <Stack direction="row" mt={1} sx={{alignItems: 'center', justifyContent: 'space-between', width: '100%', overflow: 'hidden', height: '48px'}}>
                <Select size="small" className="label" value={quantityType} sx={{width: '50%', marginRight: '8px'}} onChange={changeTypeQuantity}>
                  <MenuItem value="quantity">Количество генерируемых лицензий</MenuItem>
                  <MenuItem value="range">Для заводских номеров из диапазона</MenuItem>
                  <MenuItem value="file">Для заводских номеров из файла</MenuItem>
                </Select>
                {renderQuantityType()}
              </Stack>
              <Tabs setActiveTab={setActiveTab}>              
                <Profiles key='prof' value='prof' title='Профили' ref={profilesRef} apiRef={gridRefs.prof} 
                    icon={<ShoppingBagOutlined/>} items={profiles} onChange={subjectChange}/>
                <Apps key='app' value='app' title='Приложения' ref={appsRef} apiRef={gridRefs.app} 
                    icon={<AppsOutlined/>} items={apps} onChange={subjectChange}/>
              </Tabs>
            </CardContent>
            <CardActions sx={{px: 2}}>
              <ConfirmationDialog 
                title="Сгенерировать лицензии"
                text={dialogText()}
                open={openConfirmDialog}
                onOk={generate}
                onCancel={()=>setOpenConfirmDialog(false)}
              />
              <InfoDialog
                title="Сгенерировать лицензии"
                action={{id: 'openInfoTab', label: 'Показать в отдельной вкладке'}}
                open={openInfoDialog}
                text={infoText(createLicencesData?.body)}
                onOk={infoHandler}
              />
              <Button
                fullWidth
                disabled={isGenerateButtonDisabled()}
                startIcon={<ApiIcon/>}
                color='error'
                variant='contained'
                sx={{my: 1}}
                onClick={()=>setOpenConfirmDialog(true)}
              >Сгенерировать</Button>    
            </CardActions>
          </Card>
        </div>
    </div>      
    
  );
});

export default LicenceGenerator;